/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injector, Type, inject } from '@angular/core';
import { CacheInjectionOptions, CacheOptions } from './interfaces';
import { CacheService } from './cache.service';

export class CacheFactory {
  /**
   * Inject a service from Angular DI and apply cache configuration.
   * @param service Service class to inject
   * @param opt cache configuration
   * @returns Instance of the Service class, using the cache configuration
   *
   * @example ```ts
   * const service = inject(MyService); // no cache
   * const cachedService = CacheFactory.inject(MyService, cacheConfig); // with cache
   * ```
   */
  static inject<T>(service: Type<T>, opt: CacheInjectionOptions<T>): T {
    const cache = new CacheService<T>();
    const serviceInstance = CacheFactory.createInstance(service, opt.injector);
    cache.applyConfig(serviceInstance, opt.methods);
    return serviceInstance;
  }

  /**
   * Provide a class reference to the Angular DI.
   * The provided class will be a subclass of the original class with the cache configuration applied.
   * @param classRef Class reference to provide
   * @param opt cache configuration
   * @returns Class reference with cache configuration applied
   *
   * @example ```ts
   * providers: [
   *   { provide: MyService, useClass: CacheFactory.provide(MyService, cacheConfig) },
   * ],
   * ```
   */
  static provide<T extends Type<object>>(classRef: T, opt: CacheOptions<InstanceType<T>>): T {
    class CacheClass extends classRef {}
    // look for Angular provider metadata
    const key = 'ɵprov';
    const provRef = Object.getOwnPropertyDescriptor(classRef, key);
    if (provRef) {
      // copy original class' metadata
      const prov = { ...provRef };
      if (prov.value?.factory) {
        // replace the provider factory adding the cache
        const currentFactoryFn = prov.value?.factory;
        const cacheFactoryFn = (...args: any[]) => {
          const instance = new CacheService<T>();
          const serviceInstance = currentFactoryFn(...args);
          instance.applyConfig(serviceInstance, opt.methods);
          return serviceInstance;
        };
        prov.value.factory = cacheFactoryFn;
      }
      Object.defineProperty(CacheClass, key, prov);
    }
    return CacheClass;
  }

  protected static createInstance<T>(service: Type<T>, injector?: Injector): T {
    return injector ? injector.get(service) : inject(service);
  }
}
